import { useCallback, FocusEvent } from 'react'
import { useCheckIsMobileNumber } from '@user/store/utilities/hooks/useCheckIsMobileNumber'
import {
  CreateLocationFromAgreementProps,
  CreateServiceLocationFormModel,
} from '../utility/types'
import { useEmailCollection } from '@user/components/EmailCollection/useEmailCollection'
import { scrollIntoView } from '../utility/functions'
import { updatePhoneValueSearch } from '@sales-center/common'
import { useFormikContext } from 'formik'

export function useFormHandlers(location: CreateServiceLocationFormModel) {
  const path = `locations.${location.id}.`

  const { setFieldValue, setFieldTouched, errors } =
    useFormikContext<CreateLocationFromAgreementProps>()

  const { isMobileNumber: isMobilePhone, execute: checkIsPhoneMobile } =
    useCheckIsMobileNumber(location.phoneNumber ?? undefined)

  const { isMobileNumber: isMobileAltPhone, execute: checkIsAltPhoneMobile } =
    useCheckIsMobileNumber(location.altPhoneNumber)

  const { addEmail, count: emailCount } =
    useEmailCollection<CreateLocationFromAgreementProps>(
      `${path}emailAddresses`,
    )

  const addEmailAddress = useCallback(() => {
    addEmail()

    setTimeout(() => {
      scrollIntoView(`${path}emailAddresses[${emailCount - 1}].emailAddress`)
    }, 500)
  }, [addEmail, location, emailCount, path])

  const addEmailDisabled = emailCount >= 3

  const onBlurPhone = useCallback(
    (event: FocusEvent<HTMLInputElement> | FocusEvent<HTMLTextAreaElement>) => {
      const { value, name } = event.target as
        | HTMLInputElement
        | HTMLTextAreaElement

      const updatedValue = updatePhoneValueSearch(value)

      if (updatedValue == '') {
        setFieldValue(
          name == `${path}phoneNumber`
            ? `${path}phoneExt`
            : `${path}altPhoneExt`,
          '',
        )
      }
      setFieldValue(name, updatedValue)

      name == `${path}phoneNumber`
        ? checkIsPhoneMobile(updatedValue)
        : checkIsAltPhoneMobile(updatedValue)

      if (errors?.locations && errors?.locations[location.id]) {
        const emailErrors: string[] =
          (errors?.locations[location.id]?.emailAddresses as string[]) ?? []

        emailErrors.forEach((_, index) => {
          setFieldTouched(`${path}emailAddresses[${index}].emailAddress`, true)
        })
      }
    },
    [setFieldValue, checkIsPhoneMobile, checkIsAltPhoneMobile, errors, path],
  )

  const handleEmailChange = useCallback(() => {
    if (errors?.locations && errors?.locations[location.id]) {
      const emailErrors: string[] =
        (errors?.locations[location.id]?.emailAddresses as string[]) ?? []

      emailErrors.forEach((_, index) => {
        setFieldTouched(`${path}emailAddresses[${index}].emailAddress`, true)
      })
    }

    setFieldTouched(`${path}phoneNumber`, true, true)
  }, [setFieldTouched, errors, path])

  return {
    isMobilePhone,
    isMobileAltPhone,
    checkIsPhoneMobile,
    checkIsAltPhoneMobile,
    addEmailAddress,
    addEmailDisabled,
    onBlurPhone,
    handleEmailChange,
  }
}
